import * as THREE from 'three';
import { GROUND } from '../core/SimConfig.js';

const FORCE_SCALE = 1 / 5000; // meters per Newton
const MIN_LENGTH = 0.5;

const COLORS = {
  thrust:   0x00ff00, // green
  lift:     0x00ccff, // cyan
  drag:     0xff4444, // red
  velocity: 0xffff00, // yellow
  wind:     0xff88ff, // magenta
};

const GEAR_COLOR_NO_CONTACT = 0xff8800; // orange — ray with no ground contact
const GEAR_COLOR_CONTACT    = 0x00ff88; // green — ray with ground contact

const _pivot = new THREE.Vector3();
const _dir = new THREE.Vector3();

const LABELS = {
  thrust:   'THRUST',
  lift:     'LIFT',
  drag:     'DRAG',
  velocity: 'VEL',
  wind:     'WIND',
};

export default class ForceViz {
  constructor(scene) {
    this.scene = scene;
    this.visible = false;
    this.arrows = {};

    for (const key of Object.keys(COLORS)) {
      const arrow = new THREE.ArrowHelper(
        new THREE.Vector3(0, 0, -1),
        new THREE.Vector3(),
        1,
        COLORS[key],
        0.8,
        0.4,
      );
      arrow.visible = false;
      scene.add(arrow);
      this.arrows[key] = arrow;
    }

    // Gear ray arrows (3 wheels: nose, left, right)
    this.gearArrows = [];
    for (let i = 0; i < 3; i++) {
      const arrow = new THREE.ArrowHelper(
        new THREE.Vector3(0, -1, 0),
        new THREE.Vector3(),
        1,
        GEAR_COLOR_NO_CONTACT,
        0.3,
        0.15,
      );
      arrow.visible = false;
      scene.add(arrow);
      this.gearArrows.push(arrow);
    }

    // Legend overlay
    this._legend = document.createElement('div');
    this._legend.id = 'force-legend';
    this._legend.innerHTML = Object.entries(COLORS)
      .map(([key, color]) => {
        const hex = '#' + color.toString(16).padStart(6, '0');
        return `<div class="force-legend-row">
          <span class="force-legend-swatch" style="background:${hex}"></span>
          <span class="force-legend-label">${LABELS[key]}</span>
          <span class="force-legend-value" id="fv-${key}"></span>
        </div>`;
      })
      .join('');
    this._legend.style.display = 'none';
    document.getElementById('app').appendChild(this._legend);

    this._valueEls = {};
    for (const key of Object.keys(COLORS)) {
      this._valueEls[key] = document.getElementById(`fv-${key}`);
    }
  }

  dispose() {
    for (const arrow of Object.values(this.arrows)) {
      this.scene.remove(arrow);
      arrow.dispose();
    }
    for (const arrow of this.gearArrows) {
      this.scene.remove(arrow);
      arrow.dispose();
    }
    this._legend.remove();
  }

  setVisible(visible) {
    this.visible = visible;
    for (const arrow of Object.values(this.arrows)) {
      arrow.visible = this.visible;
    }
    for (const arrow of this.gearArrows) {
      arrow.visible = this.visible;
    }
    this._legend.style.display = this.visible ? '' : 'none';
  }

  update(aircraft, groundContact, wind) {
    if (!this.visible || !aircraft || !aircraft.lastForces) return;

    const pos = aircraft.position;
    const forces = aircraft.lastForces;

    for (const key of Object.keys(this.arrows)) {
      const arrow = this.arrows[key];

      if (key === 'wind') {
        // Wind arrow — show wind vector direction and speed
        if (!wind) { arrow.visible = false; continue; }
        arrow.visible = this.visible;
        const mag = wind.length();
        const len = Math.max(MIN_LENGTH, mag * 0.5);
        arrow.position.copy(pos);
        if (mag > 0.1) {
          arrow.setDirection(wind.clone().normalize());
        }
        arrow.setLength(len, len * 0.12, len * 0.06);
        this._valueEls[key].textContent = `${mag.toFixed(1)} m/s`;
        continue;
      }

      const vec = forces[key];
      const mag = vec.length();

      if (key === 'velocity') {
        // Show velocity direction scaled to airspeed (1 m/s = 0.2 units)
        const len = Math.max(MIN_LENGTH, mag * 0.2);
        arrow.position.copy(pos);
        if (mag > 0.1) {
          arrow.setDirection(vec.clone().normalize());
        }
        arrow.setLength(len, len * 0.12, len * 0.06);
        this._valueEls[key].textContent = `${(mag * 3.6).toFixed(0)} km/h`;
      } else {
        const len = Math.max(MIN_LENGTH, mag * FORCE_SCALE);
        arrow.position.copy(pos);
        if (mag > 0.1) {
          arrow.setDirection(vec.clone().normalize());
        }
        arrow.setLength(len, len * 0.12, len * 0.06);
        this._valueEls[key].textContent = `${(mag / 1000).toFixed(1)} kN`;
      }
    }

    // Gear ray arrows — recompute from live world matrices (scene graph is
    // already synced at this point, so positions match the rendered frame)
    if (!groundContact) return;
    const contacts = groundContact.wheelContacts;
    for (let i = 0; i < this.gearArrows.length; i++) {
      const arrow = this.gearArrows[i];
      const wheel = groundContact.wheels[i];
      const info = contacts[i];
      if (!wheel || !info) {
        arrow.visible = false;
        continue;
      }
      arrow.visible = this.visible;

      // Read current world transform of the gear mesh (up-to-date after sync)
      wheel.obj.updateWorldMatrix(true, false);
      _pivot.setFromMatrixPosition(wheel.obj.matrixWorld);
      _dir.set(0, 0, -1).transformDirection(wheel.obj.matrixWorld);

      arrow.position.copy(_pivot);
      arrow.setDirection(_dir);
      arrow.setLength(GROUND.gearLength, 0.3, 0.15);

      const color = info.normalForce > 0 ? GEAR_COLOR_CONTACT : GEAR_COLOR_NO_CONTACT;
      arrow.setColor(color);
    }
  }
}
